import { useState, useRef, useMemo, useEffect } from 'react';
import { enablePatches, enableMapSet, produceWithPatches } from 'immer';
import isEqual from 'lodash-es/isEqual';

// src/module/store/react/index.ts
enablePatches();
enableMapSet();
function createSubscription(store, subscriber, stateSelector) {
  let lastState = store.getCurrentState(stateSelector);
  const queue = [];
  let subscriberIsBusy = false;
  const act = () => {
    const nextState = store.getCurrentState(stateSelector);
    if (!isEqual(nextState, lastState)) {
      subscriberIsBusy = true;
      subscriber(nextState, lastState);
      subscriberIsBusy = false;
      lastState = nextState;
    }
  };
  return () => {
    if (subscriberIsBusy) {
      queue.push(() => act());
      return;
    }
    act();
    while (queue.length) {
      const nextAct = queue.shift();
      nextAct?.();
    }
  };
}
var Store = class {
  _subscribers = [];
  initialState;
  currentState;
  _fireSubscribers() {
    this._subscribers.forEach((s) => s());
  }
  constructor(state) {
    this.initialState = { ...state };
    this.currentState = { ...state };
  }
  subscribe(stateSelector, subscriber) {
    const sub = createSubscription(this, subscriber, stateSelector);
    this._subscribers.push(sub);
    const unsub = () => {
      this._subscribers = this._subscribers.filter((s) => s !== sub);
    };
    return unsub;
  }
  resetToInitialState() {
    this.currentState = this.getInitialState();
    this._fireSubscribers();
  }
  update(updater) {
    if (updater instanceof Function) {
      const [result, patches] = produceWithPatches(this.currentState, updater);
      if (!patches.length) {
        return result;
      }
      this.currentState = result;
    } else if (typeof updater === "object") {
      this.currentState = { ...updater };
    }
    this._fireSubscribers();
    return this.currentState;
  }
  getInitialState() {
    return { ...this.initialState };
  }
  getCurrentState(stateSelector) {
    const currentState = { ...this.currentState };
    return stateSelector ? stateSelector(currentState) : currentState;
  }
};

// src/module/store/react/index.ts
function useStore(store, stateSelector) {
  const [state, setState] = useState(store.getCurrentState(stateSelector));
  const isMounted = useRef(true);
  const unsub = useMemo(() => {
    return store.subscribe(stateSelector, (nextState) => {
      if (!isMounted.current) {
        return;
      }
      setState((_) => nextState);
    });
  }, []);
  useEffect(() => {
    return () => {
      isMounted.current = false;
      unsub();
    };
  }, []);
  return state;
}
var Store2 = class extends Store {
  useState = (stateSelector) => {
    return useStore(this, stateSelector);
  };
};

export { Store2 as Store, useStore };
